import type { OnInit} from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { CustomTooltipDirective } from '../../shared/directives/custom-tooltip.directive';
import { CycleManagementService } from '../../core/services/cycle-management.service';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { SharePointService } from '../../core/services/sharepoint.service';
import { SnackbarService } from '../../core/services/snackbar.service';
import { AuthService } from '../../core/services/auth.service';
import type { CycleQuestionAnswer, AssignedCycleForm as _AssignedCycleForm, CycleFormQuestion as _CycleFormQuestion, CycleQuestionAnswer as _CycleQuestionAnswer, ActivityData } from '../../shared/models';
import { ActivityTabComponent } from '../manager-form/components/activity-tab/activity-tab.component';
import type { FormQuestion, FormStage, Form, Manager, ManagerCycleFormData } from './models';

// ===================================================================
// COMPONENT
// ===================================================================
/**
 * ViewManagerCycleFormComponent
 *
 * Displays and manages cycle forms for managers with the following features:
 * - View assigned forms and activities
 * - Display questions grouped by stages
 * - Filter questions with "No" answers
 * - Activity-based navigation
 * - Decision approval workflow for Compliance BP
 * - Overall input tracking (root cause, mitigation actions)
 *
 * Refactoring improvements:
 * - Extracted constants for magic strings
 * - Consolidated duplicate dialog logic
 * - Created helper methods for answer mapping
 * - Improved error handling
 * - Reduced method complexity
 */
@Component({
  selector: 'app-view-manager-cycle-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    MatCardModule,
    MatCheckboxModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    ActivityTabComponent,
    CustomTooltipDirective
  ],
  templateUrl: './view-manager-cycle-form.component.html',
  styleUrls: ['./view-manager-cycle-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewManagerCycleFormComponent implements OnInit {
  // Constants
  private readonly COMPLAINTS_DP_ROLE_CODE = 'COMPBP';
  private readonly UNTAGGED_STAGE_KEY = 'untagged';
  private readonly UNTAGGED_STAGE_ORDER = 99999;
  private readonly DEFAULT_STAGE_NAME = 'General Questions';

  // Route & User Data
  cycleId = '';
  managerId = '';
  userId = 0;

  // Loading State
  isLoading = false;
  loadingError: string | null = null;

  // Caches
  private readonly noAnswerCountsMap = new Map<number, number>();
  private readonly completionCountsMap = new Map<number, number>();
  formNoAnswersCache = new Map<number, number>();
  formNotApplicableCache = new Map<number, boolean>();
  formCompletedCache = new Map<number, boolean>();
  formPendingApprovalsCache = new Map<number, number>();
  private readonly questionNumbersCache = new Map<number, number>();

  // Activity State
  selectedActivityIndex = 0;
  selectedActivityNumber = 1;
  totalQuestionsCount = 0;
  currentActivityHasNoAnswer = false;
  currentActivityOverallInput: {
    rootCause?: string;
    rootCauseDescription?: string;
    rootCauseMarketId?: number;
    mitigationAction?: string;
    mitigationActionDescription?: string;
    confirmed?: boolean;
    comments?: string;
    cycleActivityId?: number;
    activityStatus?: 'draft' | 'submitted' | 'approved' | 'rejected';
    isActionRequiredOnReject?: boolean;
    approverComments?: string | null;
    approvedBy?: string | null;
  } = {};

  // Decision section properties
  decisionComments = '';
  isSubmittingDecision = false;
  activityDecisionStatus: 'pending' | 'accepted' | 'rejected_with_action' | 'rejected_without_action' | null = null;

  formData: ManagerCycleFormData = {
    cycleId: '',
    cycleName: '',
    managerId: '',
    managerName: '',
    selectedFormId: 0,
    selectedStageId: 0,
    showOnlyAnswersWithNo: false,
    managers: [],
    forms: []
  };

  // Computed property for filter state
  get showOnlyNo(): boolean {
    return this.formData.showOnlyAnswersWithNo;
  }

  // Get activity statistics tabs for selected form
  get activityTabs(): { activity_number: number; label: string; total_answers: number; no_answers: number; activity_status?: 'draft' | 'submitted' | 'approved' | 'rejected'; reference_number?: string; display_name?: string }[] {
    const selectedForm = this.getSelectedForm();
    if (!selectedForm?.activity_statistics || selectedForm.activity_statistics.length === 0) {
      return [];
    }
    return selectedForm.activity_statistics.map(stat => ({
      activity_number: stat.activity_number,
      label: `Activity ${stat.activity_number}`,
      total_answers: stat.total_answers || 0,
      no_answers: stat.no_answers || 0,
      activity_status: stat.activity_status,
      reference_number: stat.reference_number,
      display_name: stat.display_name
    }));
  }

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cycleManagementService = inject(CycleManagementService);
  private authService = inject(AuthService);
  private snackbarService = inject(SnackbarService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  // Check if user is Complaints DP
  get isComplaintsDP(): boolean {
    const roleContext = this.authService.getSelectedRoleContext();
    return roleContext?.role_code === this.COMPLAINTS_DP_ROLE_CODE;
  }

  // Check if current user is a Manager
  get isManager(): boolean {
    const roleContext = this.authService.getSelectedRoleContext();
    return roleContext?.role_code === 'MANAGER';
  }

  // Check if decision section should be displayed
  get shouldShowDecisionSection(): boolean {
    const hasRootCause = Boolean(
      this.currentActivityOverallInput.rootCause ||
      this.currentActivityOverallInput.rootCauseDescription
    );

    // Check if the root cause market matches the user's market
    const roleContext = this.authService.getSelectedRoleContext();
    const isCorrectMarket = roleContext?.market_id === this.currentActivityOverallInput.rootCauseMarketId;

    // Only show for submitted activities
    const isSubmitted = this.currentActivityOverallInput.activityStatus === 'submitted';

    return Boolean(
      this.isComplaintsDP &&
      this.currentActivityHasNoAnswer &&
      this.hasActivities &&
      hasRootCause &&
      isCorrectMarket &&
      isSubmitted
    );
  }

  // Check if decision status section should be shown
  get shouldShowDecisionStatusSection(): boolean {
    return Boolean(
      this.currentActivityHasNoAnswer &&
      this.hasActivities &&
      (this.currentActivityOverallInput.activityStatus === 'approved' ||
       this.currentActivityOverallInput.activityStatus === 'rejected')
    );
  }

  // Get decision status text
  get decisionStatusText(): string {
    if (this.currentActivityOverallInput.activityStatus === 'approved') {
      return 'Approved';
    }
    if (this.currentActivityOverallInput.isActionRequiredOnReject) {
      return 'Rejected with Action';
    }
    return 'Rejected without Action';
  }

  // Get approver label
  get approverLabel(): string {
    return this.currentActivityOverallInput.activityStatus === 'approved' ? 'Approved by' : 'Rejected by';
  }

  // Get decision status icon
  get decisionStatusIcon(): string {
    return this.currentActivityOverallInput.activityStatus === 'approved' ? 'check_circle' : 'cancel';
  }

  // Check if current activity is approved
  get isApprovedStatus(): boolean {
    return this.currentActivityOverallInput.activityStatus === 'approved';
  }

  // Check if current activity is rejected
  get isRejectedStatus(): boolean {
    return this.currentActivityOverallInput.activityStatus === 'rejected';
  }

  // Get decision workflow status text for display
  get decisionWorkflowStatusText(): string {
    if (this.activityDecisionStatus === 'accepted') {
      return 'Decision: Accepted';
    }
    if (this.activityDecisionStatus === 'rejected_with_action') {
      return 'Decision: Rejected With Action';
    }
    if (this.activityDecisionStatus === 'rejected_without_action') {
      return 'Decision: Rejected Without Action';
    }
    return '';
  }

  // Get decision workflow status icon
  get decisionWorkflowStatusIcon(): string {
    return this.activityDecisionStatus === 'accepted' ? 'check_circle' : 'cancel';
  }

  // Check if decision is accepted
  get isDecisionAccepted(): boolean {
    return this.activityDecisionStatus === 'accepted';
  }

  // Check if decision is rejected
  get isDecisionRejected(): boolean {
    return this.activityDecisionStatus === 'rejected_with_action' ||
           this.activityDecisionStatus === 'rejected_without_action';
  }

  // Get monitoring status icon
  get monitoringStatusIcon(): string {
    return this.currentActivityOverallInput.confirmed ? 'check_circle' : 'cancel';
  }

  // Get monitoring status text
  get monitoringStatusText(): string {
    return this.currentActivityOverallInput.confirmed ? 'Confirmed' : 'Not Confirmed';
  }

  // Check if monitoring is confirmed
  get isMonitoringConfirmed(): boolean {
    return Boolean(this.currentActivityOverallInput.confirmed);
  }

  // Get root cause display text
  get rootCauseDisplayText(): string {
    return this.currentActivityOverallInput.rootCauseDescription ||
           this.currentActivityOverallInput.rootCause ||
           'Not provided';
  }

  // Get mitigation action display text
  get mitigationActionDisplayText(): string {
    return this.currentActivityOverallInput.mitigationActionDescription ||
           this.currentActivityOverallInput.mitigationAction ||
           'Not provided';
  }

  // Check if should show overall input section
  get shouldShowOverallInput(): boolean {
    return this.currentActivityHasNoAnswer && this.hasActivities;
  }

  // Count activities with "submitted" status for pending approvals
  get pendingApprovalsCount(): number {
    const selectedForm = this.getSelectedForm();
    if (!selectedForm?.activity_statistics || selectedForm.activity_statistics.length === 0) {
      return 0;
    }
    return selectedForm.activity_statistics.filter(
      stat => stat.activity_status === 'submitted'
    ).length;
  }

  ngOnInit(): void {
    // Subscribe to role context changes to trigger change detection
    this.authService.selectedRoleContext$.subscribe(() => {
      this.cdr.markForCheck();
    });

    this.route.params.subscribe(params => {
      this.cycleId = params['cycleId'];
      this.managerId = params['managerId'];
      this.userId = Number(this.managerId); // Convert managerId to number for API calls

      // Update form data with route parameters
      if (this.cycleId && this.managerId) {
        // Get manager name from navigation state
        const managerName = window.history.state?.managerName;

        if (managerName) {
          this.formData.managerName = managerName;
          this.cdr.markForCheck();
        }

        this.loadFormData();
      }
    });
  }

  onAcceptDecision(): void {
    if (this.isSubmittingDecision || !this.validateDecisionComments()) return;
    this.openDecisionDialog(
      'Confirm Acceptance',
      'Are you sure you want to accept this activity?',
      'Accept',
      'info',
      () => this.submitDecision('accepted')
    );
  }

  onRejectWithAction(): void {
    if (this.isSubmittingDecision || !this.validateDecisionComments()) return;
    this.openDecisionDialog(
      'Confirm Rejection with Action',
      'Are you sure you want to reject this activity with action required?',
      'Reject with Action',
      'warning',
      () => this.submitDecision('rejected_with_action')
    );
  }

  onRejectWithoutAction(): void {
    if (this.isSubmittingDecision || !this.validateDecisionComments()) return;
    this.openDecisionDialog(
      'Confirm Rejection without Action',
      'Are you sure you want to reject this activity without action required?',
      'Reject without Action',
      'danger',
      () => this.submitDecision('rejected_without_action')
    );
  }

  private validateDecisionComments(): boolean {
    if (!this.decisionComments.trim()) {
      this.snackbarService.showError('Comments are required for decision.');
      return false;
    }
    return true;
  }

  private openDecisionDialog(
    title: string,
    message: string,
    confirmText: string,
    type: 'info' | 'warning' | 'danger',
    onConfirm: () => void
  ): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { title, message, confirmText, cancelText: 'Cancel', type },
      width: '480px',
      disableClose: true,
      panelClass: 'modern-dialog',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) onConfirm();
    });
  }

  private getApproverPrid(): string | null {
    const approverPrid = this.authService.getProfile()?.prid || '';
    if (!approverPrid) {
      this.snackbarService.showError('Unable to identify user. Please log in again.');
      this.isSubmittingDecision = false;
      return null;
    }
    return approverPrid;
  }

  private getDecisionApiCall(
    decision: 'accepted' | 'rejected_with_action' | 'rejected_without_action',
    cycleActivityId: number,
    approverPrid: string
  ): ReturnType<typeof this.cycleManagementService.approveActivity> | ReturnType<typeof this.cycleManagementService.rejectActivity> {
    return decision === 'accepted'
      ? this.cycleManagementService.approveActivity(cycleActivityId, approverPrid, this.decisionComments)
      : this.cycleManagementService.rejectActivity(
          cycleActivityId,
          approverPrid,
          this.decisionComments,
          decision === 'rejected_with_action'
        );
  }

  private submitDecision(decision: 'accepted' | 'rejected_with_action' | 'rejected_without_action'): void {
    this.isSubmittingDecision = true;

    const selectedForm = this.getSelectedForm();
    if (!selectedForm?.cycle_assignment_id) {
      this.handleDecisionError('Unable to submit decision. Form information is missing.');
      return;
    }

    const cycleActivityId = this.currentActivityOverallInput.cycleActivityId;
    if (!cycleActivityId) {
      this.handleDecisionError('Unable to find activity ID. Please refresh and try again.');
      return;
    }

    const approverPrid = this.getApproverPrid();
    if (!approverPrid) return;

    const apiCall = this.getDecisionApiCall(decision, cycleActivityId, approverPrid);

    apiCall.subscribe({
      next: (response) => this.handleDecisionSuccess(response, decision),
      error: (err) => this.handleDecisionError(err.error?.message || 'Failed to submit decision. Please try again.', err)
    });
  }

  private handleDecisionSuccess(response: { message?: string }, decision: string): void {
    this.activityDecisionStatus = decision as 'accepted' | 'rejected_with_action' | 'rejected_without_action';
    this.snackbarService.showSuccess(response.message || `Decision ${decision.replace(/_/g, ' ')} submitted successfully!`);
    this.decisionComments = '';
    this.isSubmittingDecision = false;
    this.loadFormData();
  }

  private handleDecisionError(message: string, error?: { error?: { message?: string } }): void {
    this.snackbarService.showError(message);
    if (error) console.error('Decision submission error:', error);
    this.isSubmittingDecision = false;
    this.cdr.markForCheck();
  }

  loadFormData(): void {
    this.isLoading = true;
    this.loadingError = null;

    const cycleIdNum = Number(this.cycleId);
    const userIdNum = Number(this.userId);

    // Preserve currently selected form's cycle_form_id and activity before reload
    const selectedForm = this.getSelectedForm();
    const previouslySelectedCycleFormId = selectedForm?.cycle_form_id;
    const previouslySelectedActivityNumber = this.selectedActivityNumber;

    // Call API to get assigned forms
    this.cycleManagementService.getAssignedForms(cycleIdNum, userIdNum).subscribe({
      next: (response) => {
        this.formData.cycleId = this.cycleId;
        this.formData.managerId = this.managerId;

        // Set cycle name from first form if available
        if (response.assigned_forms.length > 0) {
          this.formData.cycleName = response.assigned_forms[0].cycle_name || '';
        }

        // Transform API response to component format
        this.formData.forms = response.assigned_forms.map((apiForm, index) => ({
          id: index + 1,
          name: apiForm.form_name,
          type: 'Form' as const,
          status: apiForm.activity_status || 'draft',
          isSelected: index === 0,
          cycle_form_id: apiForm.cycle_form_id,
          form_code: apiForm.form_code,
          cycle_assignment_id: apiForm.cycle_assignment_id,
          activity_count: apiForm.activity_count,
          completion_percentage: apiForm.completion_percentage,
          no_answers: apiForm.no_answers,
          activity_statistics: apiForm.activity_statistics || [],
          form_deadline_date: apiForm.form_deadline_date || '',
          cycleName: apiForm.cycle_name || '',
          is_form_not_applicable: apiForm.is_form_not_applicable,
          form_not_applicable_reason: apiForm.form_not_applicable_reason,
          cycle_assignment_status: apiForm.cycle_assignment_status
        }));

        // Restore previously selected form or default to first form
        if (this.formData.forms.length > 0) {
          // Try to restore the previously selected form by cycle_form_id
          const formToSelect = previouslySelectedCycleFormId
            ? this.formData.forms.find(f => f.cycle_form_id === previouslySelectedCycleFormId) || this.formData.forms[0]
            : this.formData.forms[0];

          // Update isSelected property for all forms
          this.formData.forms.forEach(f => f.isSelected = (f.id === formToSelect.id));
          this.formData.selectedFormId = formToSelect.id;

          // Restore previously selected activity or default to first activity
          if (previouslySelectedActivityNumber > 0 && formToSelect.activity_statistics) {
            const activityExists = formToSelect.activity_statistics.some(
              stat => stat.activity_number === previouslySelectedActivityNumber
            );
            if (activityExists) {
              this.selectedActivityNumber = previouslySelectedActivityNumber;
            } else if (formToSelect.activity_statistics.length > 0) {
              this.selectedActivityNumber = formToSelect.activity_statistics[0].activity_number;
            }
          } else if (formToSelect.activity_statistics && formToSelect.activity_statistics.length > 0) {
            this.selectedActivityNumber = formToSelect.activity_statistics[0].activity_number;
          }

          this.loadFormQuestions(formToSelect);
        }

        this.rebuildCaches();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading assigned forms:', error);
        this.loadingError = 'Failed to load form data. Please try again.';
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private getStageKey(stageId: number | null | undefined): string {
    return stageId?.toString() || this.UNTAGGED_STAGE_KEY;
  }

  private createStage(apiQuestion: Pick<_CycleFormQuestion, 'stage_id' | 'stage_name' | 'stage_code' | 'stage_order'>): FormStage {
    return {
      id: apiQuestion.stage_id || 0,
      name: apiQuestion.stage_name || this.DEFAULT_STAGE_NAME,
      stage_code: apiQuestion.stage_code || undefined,
      stage_order: apiQuestion.stage_id ? (apiQuestion.stage_order || 0) : this.UNTAGGED_STAGE_ORDER,
      questions: []
    };
  }

  private clearQuestionAnswer(question: FormQuestion): void {
    question.answer = undefined;
    question.comments = undefined;
    question.attachment = undefined;
    question.activity_number = undefined;
  }

  private mapAnswerToQuestion(question: FormQuestion, apiAnswer: CycleQuestionAnswer): void {
    question.activity_number = apiAnswer.activity_number;
    question.comments = apiAnswer.comments || undefined;
    question.attachment = apiAnswer.file_name || undefined;

    if (question.type === 'yes_no' || question.type === 'yes_no_na') {
      const answerValue = String(apiAnswer.answer).toLowerCase();
      if (answerValue === 'yes') question.answer = 'yes';
      else if (answerValue === 'no') question.answer = 'no';
      else if (answerValue === 'na' || answerValue === 'n/a') question.answer = 'na';
    } else if (question.type === 'number') {
      question.answer = Number(apiAnswer.answer);
    } else if (question.type === 'text') {
      question.answer = apiAnswer.answer;
    }
  }

  loadFormQuestions(form: Form): void {
    if (!form.cycle_form_id) return;

    const cycleIdNum = Number(this.cycleId);

    // Load questions for the form
    this.cycleManagementService.getCycleFormQuestions(cycleIdNum, form.cycle_form_id).subscribe({
      next: (response) => {
        // Group questions by stage
        const stagesMap = new Map<string, FormStage>();

        response.questions.forEach((apiQuestion) => {
          const stageKey = this.getStageKey(apiQuestion.stage_id);

          if (!stagesMap.has(stageKey)) {
            stagesMap.set(stageKey, this.createStage(apiQuestion));
          }

          const question: FormQuestion = {
            id: apiQuestion.question_id,
            text: apiQuestion.question_text_en,
            type: apiQuestion.question_type,
            question_code: apiQuestion.question_code,
            is_comment_mandatory_on_no: apiQuestion.is_comment_mandatory_on_no,
            is_attachment_mandatory_on_no: apiQuestion.is_attachment_mandatory_on_no,
            display_order: apiQuestion.display_order,
            options: apiQuestion.question_type === 'yes_no_na' ? ['Yes', 'No', 'N/A'] :
                     apiQuestion.question_type === 'yes_no' ? ['Yes', 'No'] : undefined
          };

          this.enrichQuestion(question);
          stagesMap.get(stageKey)!.questions.push(question);
        });

        // Convert map to array and sort by stage order (untagged will be last)
        form.stages = Array.from(stagesMap.values()).sort((a, b) =>
          (a.stage_order || 0) - (b.stage_order || 0)
        );

        // Sort questions within each stage by display_order
        form.stages.forEach(stage => {
          stage.questions.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        });

        // Set default selected stage
        if (form.stages.length > 0) {
          this.formData.selectedStageId = form.stages[0].id;

          // Load answers only if activities exist
          if (form.cycle_assignment_id && (form.activity_count || 0) > 0) {
            this.loadFormAnswers(form);
          }
        }

        this.rebuildCaches();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading form questions:', error);
      }
    });
  }

  loadFormAnswers(form: Form): void {
    if (!form.cycle_assignment_id) return;

    // Load answers for all activities (no activity_number filter)
    this.cycleManagementService.getCycleAssignmentAnswers(form.cycle_assignment_id).subscribe({
      next: (response) => {
        // Check if response has activities
        if (!response || !response.activities) {
          this.cdr.markForCheck();
          return;
        }

        // Group answers by activity number and question ID from new structure
        const answersByActivityAndQuestion = new Map<string, CycleQuestionAnswer>();

        if (response.activities && Array.isArray(response.activities)) {
          response.activities.forEach((activityData) => {
            const activityNumber = activityData.activity_details?.activity_number;
            if (!activityNumber || !activityData.answers) return;

            // Update activity_statistics with reference_number and display_name
            const activityStat = form.activity_statistics?.find(stat => stat.activity_number === activityNumber);
            if (activityStat && activityData.activity_details) {
              activityStat.reference_number = activityData.activity_details.reference_number;
              // Filter out "unknown" or "Unknown" values - treat as empty
              const displayName = activityData.activity_details.display_name || '';
              activityStat.display_name = (displayName.toLowerCase() === 'unknown') ? '' : displayName;
            }

            activityData.answers.forEach((answerData) => {
              const key = `${activityNumber}_${answerData.question_id}`;
              // Convert to CycleQuestionAnswer format for compatibility
              const cycleAnswer: CycleQuestionAnswer = {
                question_id: answerData.question_id,
                answer_id: answerData.answer_id,
                activity_number: activityNumber,
                answer: answerData.answer as 'yes' | 'no' | 'na' | 'Y' | 'N',
                comments: answerData.comments ?? null,
                reference_number: activityData.activity_details?.reference_number ?? null,
                is_monitoring_complete: activityData.activity_details?.is_monitoring_complete ?? false,
                answer_created_at: activityData.activity_details?.created_at ?? '',
                answer_updated_at: activityData.activity_details?.updated_at ?? '',
                answer_updated_by: activityData.activity_details?.updated_by ?? null,
                activity_status: activityData.activity_status?.activity_status,
                root_cause_id: activityData.activity_details?.root_cause_id ?? null,
                root_cause_code: activityData.activity_details?.root_cause_code ?? null,
                root_cause_description: activityData.activity_details?.root_cause_description ?? null,
                root_cause_market_id: activityData.activity_details?.root_cause_market_id ?? null,
                mitigating_action_id: activityData.activity_details?.mitigating_action_id ?? null,
                mitigating_action_description: activityData.activity_details?.mitigating_action_description ?? null,
                attachment_id: answerData.attachment_id ?? null,
                file_name: answerData.file_name ?? null,
                file_path: answerData.file_path ?? null,
                file_size: answerData.file_size ?? null,
                file_type: answerData.file_type ?? null,
                attachment_uploaded_at: answerData.attachment_uploaded_at ?? null,
                attachment_uploaded_by: answerData.attachment_uploaded_by ?? null,
                compliance_status: answerData.compliance_status ?? 'unanswered',
                risk_management_status: activityData.risk_management_status as 'not_applicable' | 'missing_root_cause' | 'missing_mitigation' | 'monitoring_incomplete' | 'risk_management_complete'
              };
              answersByActivityAndQuestion.set(key, cycleAnswer);
            });
          });
        }

        // Store all answers for activity switching
        form.allAnswers = answersByActivityAndQuestion;

        // Extract Overall Input data for the selected activity
        this.extractOverallInputForActivity(response.activities, this.selectedActivityNumber);

        // Map answers to questions for the currently selected activity - create new stages array for immutability
        // Use the actual selectedActivityNumber instead of calculating from index
        const selectedActivityNumber = this.selectedActivityNumber;
        let _matchedAnswers = 0;
        let _totalQuestions = 0;

        form.stages = form.stages?.map((stage) => ({
          ...stage,
          questions: stage.questions.map((question) => {
            _totalQuestions++;
            const key = `${selectedActivityNumber}_${question.id}`;
            const apiAnswer = answersByActivityAndQuestion.get(key);

            const updatedQuestion = { ...question };

            if (apiAnswer) {
              _matchedAnswers++;
              this.mapAnswerToQuestion(updatedQuestion, apiAnswer);
            } else {
              this.clearQuestionAnswer(updatedQuestion);
            }

            // Enrich question with computed properties
            this.enrichQuestion(updatedQuestion);
            return updatedQuestion;
          })
        }));

        // Check if current activity has any "No" answers
        this.updateCurrentActivityHasNoAnswer(form);

        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading form answers:', error);
      }
    });
  }

  private extractOverallInputForActivity(activities: ActivityData[], activityNumber: number): void {
    const activityData = activities?.find(a => a.activity_details?.activity_number === activityNumber);

    if (activityData?.activity_details) {
      const details = activityData.activity_details;

      // Get approver info from activity_statistics in the current form if available
      const currentFormStats = this.selectedForm?.activity_statistics?.find(
        stat => stat.activity_number === activityNumber
      );

      this.currentActivityOverallInput = {
        cycleActivityId: details.cycle_activity_id,
        rootCause: details.root_cause_id?.toString(),
        rootCauseDescription: details.root_cause_description ?? undefined,
        rootCauseMarketId: details.root_cause_market_id ?? undefined,
        mitigationAction: details.mitigating_action_id?.toString(),
        mitigationActionDescription: details.mitigating_action_description ?? undefined,
        confirmed: details.is_monitoring_complete || false,
        comments: details.root_cause_mitigation_comments ?? undefined,
        activityStatus: activityData.activity_status?.activity_status ?? currentFormStats?.activity_status,
        isActionRequiredOnReject: activityData.activity_status?.is_action_required_on_reject,
        approverComments: activityData.activity_status?.approver_comments ?? currentFormStats?.approver_comments,
        approvedBy: activityData.activity_status?.approver_display_name ?? currentFormStats?.approver_display_name
      };
    } else {
      this.currentActivityOverallInput = {};
    }
  }

  private updateCurrentActivityHasNoAnswer(form: Form): void {
    if (!form.stages) {
      this.currentActivityHasNoAnswer = false;
      return;
    }

    this.currentActivityHasNoAnswer = form.stages.some(stage =>
      stage.questions.some(question =>
        (question.type === 'yes_no' || question.type === 'yes_no_na') && question.answer === 'no'
      )
    );
  }

  navigateBack(): void {
    this.router.navigate(['/cycle-details', this.cycleId]);
  }

  navigateToCycles(): void {
    this.router.navigate(['/view-cycles']);
  }

  navigateToCycleDetails(): void {
    this.router.navigate(['/cycle-details', this.cycleId]);
  }

  selectForm(form: Form): void {
    // Update selection
    this.formData.forms.forEach(f => f.isSelected = false);
    form.isSelected = true;
    this.formData.selectedFormId = form.id;

    // Reset selected activity index and number
    this.selectedActivityIndex = 0;
    // Set initial activity number from first tab if available
    if (form.activity_statistics && form.activity_statistics.length > 0) {
      this.selectedActivityNumber = form.activity_statistics[0].activity_number;
    } else {
      this.selectedActivityNumber = 1;
    }

    // Load questions and answers if not already loaded
    if (!form.stages || form.stages.length === 0) {
      this.loadFormQuestions(form);
    } else {
      // Reset to first stage if form has stages
      if (form.stages.length > 0) {
        this.formData.selectedStageId = form.stages[0].id;
      }
      // Reload answers for the first activity if answers are already loaded
      if (form.allAnswers) {
        this.updateAnswersForSelectedActivity(form);
      }
    }

    this.cdr.markForCheck();
  }

  selectManager(manager: Manager): void {
    // Update active manager
    this.formData.managers.forEach(m => m.isActive = false);
    manager.isActive = true;
    this.formData.managerId = manager.id;
    this.formData.managerName = manager.name;
  }

  getSelectedForm(): Form | undefined {
    return this.formData.forms.find(f => f.id === this.formData.selectedFormId);
  }

  get selectedForm(): Form | undefined {
    return this.getSelectedForm();
  }

  getSelectedStage(): FormStage | undefined {
    const selectedForm = this.getSelectedForm();
    if (!selectedForm?.stages) return undefined;
    return selectedForm.stages.find(s => s.id === this.formData.selectedStageId);
  }

  get selectedStage(): FormStage | undefined {
    return this.getSelectedStage();
  }

  get allStages(): FormStage[] {
    const selectedForm = this.getSelectedForm();
    return selectedForm?.stages || [];
  }

  get hasActivities(): boolean {
    const selectedForm = this.getSelectedForm();
    return (selectedForm?.activity_count || 0) > 0;
  }

  getFilteredQuestions(): FormQuestion[] {
    const selectedForm = this.getSelectedForm();
    if (!selectedForm?.stages) return [];

    // Collect all questions from all stages
    const allQuestions = selectedForm.stages.flatMap(stage => stage.questions);

    if (this.formData.showOnlyAnswersWithNo) {
      return allQuestions.filter(q => q.answer === 'no');
    }

    return allQuestions;
  }

  get filteredQuestions(): FormQuestion[] {
    const questions = this.getFilteredQuestions();
    return questions.map(q => ({
      ...q,
      enrichedOptions: q.options?.map(opt => ({
        value: opt,
        isChecked: (opt === 'Yes' && q.answer === 'yes') || (opt === 'No' && q.answer === 'no') || (opt === 'N/A' && q.answer === 'na')
      })) || []
    }));
  }

  getQuestionNumber(questionId: number): number {
    // Use cached value if available
    const cached = this.questionNumbersCache.get(questionId);
    if (cached !== undefined) {
      return cached;
    }

    const selectedForm = this.getSelectedForm();
    if (!selectedForm?.stages) return 0;

    let count = 0;
    for (const stage of selectedForm.stages) {
      for (const question of stage.questions) {
        count++;
        this.questionNumbersCache.set(question.id, count);
        if (question.id === questionId) {
          return count;
        }
      }
    }
    return count;
  }

  getStageQuestionCount(stage: FormStage): number {
    return stage.questions.length;
  }

  get totalForms(): number {
    return this.formData.forms.length;
  }

  getTotalForms(): number {
    return this.totalForms;
  }

  getFormNoAnswersCount(form: Form): number {
    // Use cached value if available
    const cached = this.formNoAnswersCache.get(form.id);
    if (cached !== undefined) {
      return cached;
    }

    // Calculate and cache
    let count = 0;
    if (form.activity_statistics && form.activity_statistics.length > 0) {
      count = form.activity_statistics.reduce((total, stat) => total + (stat.no_answers || 0), 0);
    } else {
      count = form.no_answers || 0;
    }

    this.formNoAnswersCache.set(form.id, count);
    return count;
  }

  private rebuildCaches(): void {
    this.formNoAnswersCache.clear();
    this.formNotApplicableCache.clear();
    this.formCompletedCache.clear();
    this.formPendingApprovalsCache.clear();
    this.questionNumbersCache.clear();

    // Rebuild form no-answers cache and not applicable cache
    this.formData.forms.forEach(form => {
      let count = 0;
      if (form.activity_statistics && form.activity_statistics.length > 0) {
        count = form.activity_statistics.reduce((total, stat) => total + (stat.no_answers || 0), 0);
      } else {
        count = form.no_answers || 0;
      }
      this.formNoAnswersCache.set(form.id, count);

      // Cache not applicable status
      if (form.is_form_not_applicable) {
        this.formNotApplicableCache.set(form.id, true);
      }

      // Cache completed status
      if (form.cycle_assignment_status === 'Completed') {
        this.formCompletedCache.set(form.id, true);
      }

      // Cache pending approvals count
      if (form.activity_statistics && form.activity_statistics.length > 0) {
        const pendingCount = form.activity_statistics.filter(
          stat => stat.activity_status === 'submitted'
        ).length;
        this.formPendingApprovalsCache.set(form.id, pendingCount);
      }
    });

    // Rebuild question numbers cache for selected form
    const selectedForm = this.getSelectedForm();
    if (selectedForm?.stages) {
      let count = 0;
      for (const stage of selectedForm.stages) {
        for (const question of stage.questions) {
          count++;
          this.questionNumbersCache.set(question.id, count);
          // Add computed properties directly to question
          question.questionNumber = count;
          // Pre-compute answer text
          if (question.type === 'yes_no' || question.type === 'yes_no_na') {
            if (question.answer === 'yes') question.answerText = 'Yes';
            else if (question.answer === 'no') question.answerText = 'No';
            else if (question.answer === 'na') question.answerText = 'N/A';
            else question.answerText = '';
          } else {
            question.answerText = String(question.answer || '');
          }
        }
      }
      this.totalQuestionsCount = count;
    } else {
      this.totalQuestionsCount = 0;
    }
  }

  updateAnswer(questionId: number, answer: string | boolean): void {
    const stage = this.getSelectedStage();
    if (!stage) return;

    const question = stage.questions.find(q => q.id === questionId);
    if (question) {
      question.answer = answer;
    }
  }

  updateComments(questionId: number, comments: string): void {
    const stage = this.getSelectedStage();
    if (!stage) return;

    const question = stage.questions.find(q => q.id === questionId);
    if (question) {
      question.comments = comments;
    }
  }

  onCommentsChange(questionId: number, event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.updateComments(questionId, target.value);
  }

  isOptionSelected(option: string, question: FormQuestion): boolean {
    return (option === 'Yes' && question.answer === 'yes') ||
           (option === 'No' && question.answer === 'no');
  }

  getAnswerText(question: FormQuestion): string {
    return this.computeAnswerText(question);
  }

  private computeAnswerText(question: FormQuestion): string {
    if (question.type === 'yes_no' || question.type === 'yes_no_na') {
      if (question.answer === 'yes') return 'Yes';
      if (question.answer === 'no') return 'No';
      if (question.answer === 'na') return 'N/A';
    }
    return question.answer?.toString() || '';
  }

  // TrackBy functions for performance optimization
  trackByForm(index: number, form: Form): number {
    return form.id;
  }

  trackByManager(index: number, manager: Manager): string {
    return manager.id;
  }

  trackByStage(index: number, stage: FormStage): number {
    return stage.id;
  }

  trackByQuestion(index: number, question: FormQuestion): number {
    return question.id;
  }

  trackByOption(index: number, option: string | { value: string; isChecked: boolean }): string {
    return typeof option === 'string' ? option : option.value;
  }

  // Helper methods to reduce template complexity
  isRadioChecked(option: string, question: FormQuestion): boolean {
    return (option === 'Yes' && question.answer === 'yes') ||
           (option === 'No' && question.answer === 'no') ||
           (option === 'N/A' && question.answer === 'na');
  }

  getOptionValue(option: string): string {
    if (option === 'Yes') return 'yes';
    if (option === 'No') return 'no';
    return option.toLowerCase();
  }

  shouldShowQuestion(question: FormQuestion): boolean {
    return !this.showOnlyNo || question.answer === 'no';
  }

  // Enrich question with computed properties to avoid template expressions
  private enrichQuestion(question: FormQuestion): void {
    // Pre-compute enriched options with checked state
    if (question.options) {
      question.enrichedOptions = question.options.map(option => ({
        value: option,
        isChecked: (option === 'Yes' && question.answer === 'yes') ||
                   (option === 'No' && question.answer === 'no') ||
                   (option === 'N/A' && question.answer === 'na')
      }));
    }

    // Pre-compute answer text for display
    question.answerText = this.computeAnswerText(question);

    // Pre-compute whether to show comments section
    question.showComments = !!(question.comments || question.answer === 'no');

    // Pre-compute whether to show question based on filter
    // When filter is off (showOnlyNo = false), show all questions
    // When filter is on (showOnlyNo = true), only show questions with answer 'no'
    question.showQuestion = !this.formData.showOnlyAnswersWithNo || question.answer === 'no';
  }

  // Re-enrich all questions when filter changes
  onFilterChange(): void {
    const selectedForm = this.getSelectedForm();
    if (selectedForm?.stages) {
      selectedForm.stages.forEach(stage => {
        stage.questions.forEach(question => {
          this.enrichQuestion(question);
        });
      });
      this.cdr.markForCheck();
    }
  }

  // Helper to check if question type is radio button
  isRadioQuestion(type: string): boolean {
    return type === 'yes_no' || type === 'yes_no_na';
  }

  // Select activity tab
  selectActivity(index: number): void {
    this.selectedActivityIndex = index;

    // Get the actual activity_number from the activityTabs
    const tabs = this.activityTabs;
    if (tabs && tabs[index]) {
      this.selectedActivityNumber = tabs[index].activity_number;
    }

    // Reload answers for the selected activity
    const selectedForm = this.getSelectedForm();
    if (selectedForm && selectedForm.allAnswers) {
      this.updateAnswersForSelectedActivity(selectedForm);
    } else {
      console.warn('No answers available for activity switching');
    }

    this.cdr.markForCheck();
  }

  // Update answers display based on selected activity
  private updateAnswersForSelectedActivity(form: Form): void {
    const allAnswers = form.allAnswers;
    if (!allAnswers) {
      console.warn('No allAnswers map found');
      return;
    }

    const selectedActivityNumber = this.selectedActivityNumber;

    let _answersFound = 0;
    let _questionsProcessed = 0;

    // Create new stages array to trigger change detection
    form.stages = form.stages?.map((stage) => ({
      ...stage,
      questions: stage.questions.map((question) => {
        _questionsProcessed++;
        const key = `${selectedActivityNumber}_${question.id}`;
        const apiAnswer = allAnswers.get(key);

        const updatedQuestion = { ...question };

        if (apiAnswer) {
          _answersFound++;
          this.mapAnswerToQuestion(updatedQuestion, apiAnswer);
        } else {
          this.clearQuestionAnswer(updatedQuestion);
        }

        // Enrich the question
        this.enrichQuestion(updatedQuestion);
        return updatedQuestion;
      })
    }));

    // Update Overall Input for the selected activity
    // We need to extract from the original API response stored when answers were loaded
    const selectedForm = this.getSelectedForm();
    if (selectedForm?.cycle_assignment_id) {
      // Load the Overall Input data from the stored answer data
      // We'll need to reload answers to get the activity details
      this.loadFormAnswers(selectedForm);
    } else {
      // If no cycle_assignment_id, check for No answers from current form state
      this.updateCurrentActivityHasNoAnswer(form);
    }

    // Trigger change detection
    this.cdr.markForCheck();
  }

  // Get total questions count for the form
  getTotalQuestions(): number {
    const selectedForm = this.getSelectedForm();
    if (!selectedForm?.stages) return 0;
    return selectedForm.stages.reduce((total, stage) => total + stage.questions.length, 0);
  }

  // Manager Summary Card computed properties
  get totalActivityOwners(): number {
    // Count unique activity owners across all forms
    // For now, use activity_count as a proxy for activity owners
    const selectedForm = this.getSelectedForm();
    return selectedForm?.activity_count || 0;
  }

  get managerDeadline(): string {
    const selectedForm = this.getSelectedForm();
    if (!selectedForm?.form_deadline_date) return 'Not set';

    try {
      const date = new Date(selectedForm.form_deadline_date);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
    } catch {
      return selectedForm.form_deadline_date;
    }
  }

  get overallCompletion(): number {
    const selectedForm = this.getSelectedForm();
    return selectedForm?.completion_percentage || 0;
  }

  get completionStatusClass(): string {
    const completion = this.overallCompletion;
    if (completion >= 80) return 'status-high';
    if (completion >= 50) return 'status-medium';
    return 'status-low';
  }

  // Injected services
  private readonly sharePointService = inject(SharePointService);
  private readonly snackbar = inject(SnackbarService);

  /**
   * Download attachment file from SharePoint
   */
  downloadAttachment(question: FormQuestion): void {
    if (!question.attachment) {
      this.snackbar.showError('No file attached to download');
      return;
    }

    const folderPath = this.sharePointService.getCycleFolderPath();
    const fileName = question.attachment;

    this.sharePointService.downloadFile(folderPath, fileName)
      .subscribe({
        next: (blob) => {
          // Create a download link and trigger download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.snackbar.showSuccess(`File downloaded: ${fileName}`);
        },
        error: (error) => {
          console.error('Error downloading file:', error);
          this.snackbar.showError('Failed to download file. Please try again.');
        }
      });
  }
}
